import { forwardRef, InputHTMLAttributes, useEffect, useImperativeHandle, useRef } from 'react'
import { classNames } from '@/Utils/Utils'
import * as Headless from '@headlessui/react'

type TextInputSize = 'xs' | 'sm' | 'md'

const sizeClasses: Record<TextInputSize, string> = {
    xs: 'px-[calc(--spacing(2)-1px)] py-[calc(--spacing(1)-1px)] text-sm',
    sm: 'px-[calc(--spacing(3)-1px)] py-[calc(--spacing(1.5)-1px)] text-sm',
    md: 'px-[calc(--spacing(3.5)-1px)] py-[calc(--spacing(2.5)-1px)] sm:px-[calc(--spacing(3)-1px)] sm:py-[calc(--spacing(2)-1px)]',
}

export default forwardRef(function TextInput(
    {
        type = 'text',
        className = '',
        isFocused = false,
        autoSelectAll = false,
        size = 'md',
        ...props
    }: Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> & {
        isFocused?: boolean
        autoSelectAll?: boolean
        size?: TextInputSize
    },
    ref
) {
    const localRef = useRef<HTMLInputElement>(null)

    useImperativeHandle(ref, () => ({
        focus: () => localRef.current?.focus(),
        get value() {
            return localRef.current?.value
        },
        get selectionStart() {
            return localRef.current?.selectionStart
        },
        get selectionEnd() {
            return localRef.current?.selectionEnd
        },
        setSelectionRange(start: number, end: number) {
            localRef.current?.setSelectionRange(start, end)
        },
    }))

    useEffect(() => {
        if (isFocused) {
            localRef.current?.focus()
        }
    }, [isFocused])

    useEffect(() => {
        if (autoSelectAll) {
            localRef.current?.select()
        }
    }, [autoSelectAll])

    return (
        <Headless.Input
            {...props}
            type={type}
            data-slot="control"
            className={classNames(
                className,
                'block rounded-md border-stone-300 shadow-sm focus:border-stone-400 focus:ring-stone-400',
                sizeClasses[size],
                // Disabled state
                'data-disabled:border-stone-950/20 data-disabled:bg-stone-950/5 data-disabled:text-stone-950/60'
            )}
            ref={localRef}
        />
    )
})
